/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled/macro'; // for dom selector
import * as size from '@constant/size';
import * as color from '@constant/color';
import TextBlock from 'component/common/block/text-block';
import { formatDate, removeEmailDomain, removePrefix } from '@util/string';
import Line from '@component/common/line';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import { v4 as uuid4 } from 'uuid';
import Gap from '@component/common/gap';
import Spacer from '@component/common/spacer';

interface IProps {
  providerKey: string;
  name: string;
  email: string;
  description: string;
  publishDays: string[];
  subscriptionPage: string;
}

// WebArticleItemContainer와 똑같이
const WebProviderItemContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  margin: 0 auto 0 auto;
  width: 100%;
  height: 50px;
`;

const MobileProviderItemContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  height: 115px;
`;

const PublishDaysContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 5px;
`;

const WebDeleteContainer = styled.div`
  display: none;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
`;

const Wrapper = styled.div`
  cursor: pointer;
  margin: 0 auto 0 auto;
`;

// const MOBILE_TEXT_DESIGN = {
//   text: { size: 'medium', bold: true, color: color.BLACK_2 },
//   box: { alignX: 'left' },
// } as const;

const TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2 },
  box: { alignX: 'left' },
} as const;

const ICON_DESIGN = {
  icon: { size: 'medium', color: color.GRAY_2, kind: 'outlined' },
  box: { width: '30px' },
} as const;

const FirstRowContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-self: center;
  width: 100%;
`;

const FirstColumnContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-self: center;
  width: 120px;
`;

const ProviderItem: React.FC<IProps> = ({ providerKey, name, email, description, publishDays, subscriptionPage }) => {
  const handleSubscriptionClick = () => {
    window.open(subscriptionPage, '_blank');
  };

  const days = ['월', '화', '수', '목', '금', '토', '일'];
  const PublishDays = days.map((day) => {
    const isPublishDay = publishDays.includes(day);
    return (
      <TextBlock
        key={uuid4()}
        text={day}
        design={{
          text: { size: 'tiny', color: color.BLACK_2 },
          box: {
            width: '20px',
            height: '20px',
            backgroundColor: isPublishDay ? color.YELLOW_1 : color.GRAY_3,
            borderRadius: '20px',
          },
        }}
      />
    );
  });

  return (
    <Wrapper>
      {IS_IYAGIGO_MOBILE_VIEW ? (
        <MobileProviderItemContainer>
          <FirstRowContainer>
            <TextBlock text={name} design={{ text: { ...TEXT_DESIGN.text, size: 'medium', bold: true } }} />
            <TextBlock
              text={'구독하기'}
              design={{ text: { size: 'medium', color: color.GRAY_1 } }}
              click={{ handler: handleSubscriptionClick }}
            />
          </FirstRowContainer>
          <Gap height='5px' />
          <PublishDaysContainer>{PublishDays}</PublishDaysContainer>
          <Gap height='5px' />
          <TextBlock
            text={description}
            design={{ text: { ...TEXT_DESIGN.text, multiline: 2 }, box: { alignX: 'left' } }}
          />
        </MobileProviderItemContainer>
      ) : (
        <WebProviderItemContainer>
          <FirstColumnContainer>
            <TextBlock
              text={name}
              design={{
                box: { ...TEXT_DESIGN.box },
                text: { ...TEXT_DESIGN.text, oneline: true },
              }}
            />
            <TextBlock
              text={'구독하기'}
              design={{
                box: { ...TEXT_DESIGN.box },
                text: { ...TEXT_DESIGN.text, color: color.GRAY_1, size: 'tiny' },
              }}
              click={{ handler: handleSubscriptionClick }}
            />
          </FirstColumnContainer>
          <Gap width={size.MARGIN_1} />
          <TextBlock
            text={description}
            design={{
              box: { ...TEXT_DESIGN.box, width: 'calc(100% - 350px)' },
              text: { ...TEXT_DESIGN.text, oneline: true },
            }}
          />
          <Spacer />
          <PublishDaysContainer>{PublishDays}</PublishDaysContainer>
        </WebProviderItemContainer>
      )}
      <Line width='100%' color={color.GRAY_3} />
    </Wrapper>
  );
};

export default ProviderItem;
